import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import type { DraftSet } from './state/loggerDraft';
import type { useTheme } from './theme';
import type { DropsetPatch } from './dropsetDraft';
import { DropsetBlock } from './DropsetBlock';
import { HeaderTooltip } from './HeaderTooltip';

// FocusPanelDetails (timeline logger) — the dropset STEPS + read-only notes
// under the FocusPanel input. The Dropset|Myo-rep SELECTOR bar itself now lives
// in FocusPanel, directly under the qualifier row that hosts the 🔥 Burn+ chip
// (Antenore 2026-07-02: the selector should appear right below the row, not past
// the inputs). This component keeps only the per-step DropsetBlock (which
// belongs with the working-set inputs) + the read-only cue/note displays. It
// writes the SAME DraftSet.dropset data model — NO schema/save-path change.
//
// NOTE AUTHORING RETIRED (Solution C, 2026-06-04): the per-entry 📝 note editor
// moved OUT of here to the single timeline NoteComposer (one comment space →
// timeline note events). Any EXISTING DraftEntry.notes still renders read-only
// below (keep-render), but there's no longer a way to author it here.
//
// AMRAP is intentionally absent: it's set via the reps field, not a menu entry
// ([[feedback_amrap_via_reps_form]]).
export function FocusPanelDetails({
  set,
  prescriptionNote,
  entryNote,
  isBodyweight,
  onChangeSet,
  editLocked,
  theme,
}: {
  /** The bound (pending) set, or null when every set is logged. */
  set: DraftSet | null;
  /** Coach's per-set cue 🎯 for the bound set, already resolved with
   *  carry-forward (effectivePrescriptionNote) so a set-0-only cue shows on
   *  every set. Null when there's nothing to show. */
  prescriptionNote: string | null;
  /** The per-exercise note (💬) to render, already resolved by the parent
   *  with the plan fallback (entry.notes, else the program instruction on
   *  edit/repeat). Empty string when there's nothing to show. */
  entryNote: string;
  isBodyweight: boolean;
  /** Writes a dropset patch onto the bound set (reps/weight/dropset only). */
  onChangeSet: (patch: DropsetPatch) => void;
  /** Editing a past session (edit-scope §9): adding steps to an existing dropset
   *  chain is locked (addLocked); existing step VALUES + the note stay editable. */
  editLocked: boolean;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  const hasNote = entryNote.trim().length > 0;

  return (
    <View style={styles.wrap}>
      {set?.dropset ? (
        <DropsetBlock
          dropset={set.dropset}
          isBodyweight={isBodyweight}
          inputsLocked={false}
          addLocked={editLocked}
          onChange={(next) => onChangeSet({ dropset: next })}
          theme={theme}
        />
      ) : null}

      {/* Coach's per-set prescription cue (read-only, 🎯). Hydrated onto the
          bound set by planStarterHydration; the card logger rendered this on
          every SetRow — removing the card logger (v3.33.0) deleted the only
          render site, so per-set cues silently stopped showing in the timeline
          logger even though the data is intact (Antenore 2026-06-18). Same
          read-only HeaderTooltip treatment as the exercise note below.
          Carry-forward (2026-06-19): a set-0-only cue (the common case — an
          exercise-level note authored on the first set) now shows on every set
          via effectivePrescriptionNote in the parent. */}
      {prescriptionNote ? (
        <View style={styles.noteDisplay}>
          <HeaderTooltip
            label={prescriptionNote}
            leadingIcon="target"
            tip={prescriptionNote}
            maxChars={500}
            labelNumberOfLines={2}
            alwaysOn
            labelStyle={styles.noteDisplayText}
            cellStyle={styles.noteCell}
            a11yLabel={t('mobile.logger.prescriptionReadA11y', {
              defaultValue: 'Prescribed set cue — tap to read in full',
            })}
            theme={theme}
          />
        </View>
      ) : null}

      {/* Read-only keep-render of an EXISTING entry note (authoring retired —
          new notes go through the timeline NoteComposer). Truncated to 2
          lines; a tap shows the FULL text for a few seconds via the
          transient tooltip — content, not a hint, so it bypasses the
          Hints gate (Antenore 2026-06-12). */}
      {hasNote ? (
        <View style={styles.noteDisplay}>
          <HeaderTooltip
            label={entryNote}
            leadingIcon="comment"
            tip={entryNote}
            maxChars={500}
            labelNumberOfLines={2}
            alwaysOn
            labelStyle={styles.noteDisplayText}
            cellStyle={styles.noteCell}
            a11yLabel={t('mobile.logger.noteReadA11y', {
              defaultValue: 'Exercise note — tap to read in full',
            })}
            theme={theme}
          />
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s2 },
    noteDisplay: {
      borderRadius: r.r2,
      backgroundColor: p.surface2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
    },
    noteDisplayText: { color: p.ink2, fontSize: ty.sizes.sm },
    // Left-align the tooltip trigger (HeaderTooltip centres by default for
    // its column-header use).
    noteCell: { alignItems: 'flex-start', alignSelf: 'stretch' },
  });
}
